<script setup lang="ts">
import { TETopLabelLayout, TETextInput, TEButton } from '@ben/vite-te'
import { RadioGroup, RadioGroupOption } from '@headlessui/vue'
import '@iframe-resizer/child'
import axios from 'axios'

import type { Campaign, CampaignType, EmailCampaignRecipient } from '@/js/Types/laravel'

const props = defineProps<{
  campaign: Campaign<CampaignType>
  recipients?: EmailCampaignRecipient[]
}>()

const hasSigned = ref<boolean>(false)

const hasGrouping = computed(() => {
  return props.recipients?.some((r) => r.grouping_name !== null && r.grouping_name !== '')
})

const selectedGrouping = ref<string>('Bitte wählen')

watch(selectedGrouping, () => {
  if (hasGrouping.value) {
    form.recipient_id = recipientsForSelectedGrouping.value?.[0]?.id
  }
})

const groupings = computed(() => {
  if (!hasGrouping.value) {
    return []
  }
  return (
    props.recipients
      ?.map(function (r) {
        return { name: r.grouping_name }
      })
      //remove duplicates
      .filter((value, index, self) => {
        return self.findIndex((item) => item.name === value.name) === index
      })
  )
})

const recipientsForSelectedGrouping = computed(() => {
  if (!hasGrouping.value) {
    return props.recipients
  }
  return props.recipients?.filter((r) => r.grouping_name === selectedGrouping.value)
})

function postMessageToParent(message: string) {
  if (window.parent) {
    window.parent.postMessage({ type: 'uff', data: message }, '*')
  }
}

const form = useForm<{
  first_name: string
  last_name: string
  email: string
  zip: string
  recipient_id: number | undefined
}>({
  first_name: '',
  last_name: '',
  email: '',
  zip: '',
  recipient_id: props.recipients ? props.recipients[0]?.id : undefined,
})
window.addEventListener('message', (event) => {
  if (event.origin !== 'https://fridaysforfuture.de') {
    // Validate the origin
    return
  }
  const parentUrl = event.data.url

  if (parentUrl) {
    // eslint-disable-next-line no-console
    console.log('Parent URL:', parentUrl)
    const urlObj = new URL(parentUrl)

    // Use URLSearchParams to extract parameters from the URL's query string
    const params = new URLSearchParams(urlObj.search)

    const firstName = params.get('first_name')
    if (firstName) {
      form.first_name = firstName
    }
    const lastName = params.get('last_name')
    if (lastName) {
      form.last_name = lastName
    } else if (firstName && firstName.includes(' ')) {
      const names = firstName.split(' ')
      form.first_name = names[0] ?? ''
      form.last_name = names[1] ?? ''
    }
    const plz = params.get('plz')
    if (plz) {
      form.zip = plz
    }
    const email = params.get('email')
    if (email) {
      form.email = email
    }
  }
})

const mailToLinkDone = ref('')
const clickedFallback = ref(false)
const mailTextDone = ref('')
const generalError = ref<string | null>(null)

function sign() {
  if (props.campaign.campaign_type === 'email' && props.recipients) {
    //select a random recipient form the list
    const recipient = props.recipients.find((r) => r.id === form.recipient_id)
    if (!recipient) {
      return
    }

    mailTextDone.value =
      props.campaign.email_campaign?.email_text
        .replaceAll('$NAME_POL', recipient.name ?? '')
        .replaceAll('$GROUP_POL', recipient.grouping_name ?? '')
        .replaceAll('$LASTNAME', form.last_name)
        .replaceAll('$NAME', form.first_name) ?? ''

    const subject = props.campaign.email_campaign?.subject

    const mailtoLink = `mailto:${recipient.email}?bcc=newsletter+counter@fridaysforfuture.de&subject=${subject}&body=${mailTextDone.value.replaceAll(/(?:\n)/g, '%0D%0A')}`
    console.warn(mailtoLink)

    mailToLinkDone.value = mailtoLink

    globalThis.location.href = mailtoLink
    hasSigned.value = true
  }

  if (props.campaign.campaign_type === 'petition') {
    form.clearErrors()
    axios
      .post(route('campaign.participate', { campaign: props.campaign.id }), form.data())
      .then(() => {
        hasSigned.value = true
        postMessageToParent('signed')
      })
      .catch((error) => {
        console.error(error)
        if (error.response && error.response.status === 422) {
          form.errors = error.response.data.errors
          form.errors.first_name = error.response.data.errors.first_name?.[0]
          form.errors.last_name = error.response.data.errors.last_name?.[0]
          form.errors.email = error.response.data.errors.email?.[0]
          form.errors.zip = error.response.data.errors.zip?.[0]

          generalError.value = error.response.data.errors.general?.[0] || null
        }
      })
  }
}
</script>

<template>
  <div class="mx-4 max-w-5xl px-2 sm:mx-auto sm:px-0" data-iframe-size>
    <main v-if="campaign.is_active" class="-mx-4 mb-4 sm:mx-4">
      <TransitionGroup
        name="list"
        tag="div"
        enter-active-class="transition transform duration-500 ease-out"
        leave-active-class="transition transform duration-500 ease-in"
        enter-from-class="translate-x-full opacity-0"
        enter-to-class="translate-x-0 opacity-100"
        leave-from-class="translate-x-0 opacity-100"
        leave-to-class="-translate-x-full opacity-0"
      >
        <div v-if="!hasSigned" class="top-0 flex flex-wrap rounded-3xl py-4">
          <div class="w-full bg-[#1b7340] px-2 text-[2.25rem] font-extrabold text-white">
            {{ campaign.customization_button_text ?? 'Jetzt unterschreiben' }}
          </div>

          <form class="w-full" @submit.prevent>
            <div class="mt-4 w-full gap-4 sm:grid sm:grid-cols-2">
              <template v-if="campaign.campaign_type === 'email'">
                <!-- Empfänger auswählen -->
                <fieldset class="col-span-2 w-full">
                  <legend class="text-sm/6 font-semibold text-gray-900">Empfänger</legend>

                  <template v-if="groupings && groupings.length > 0">
                    <p class="mt-1 text-sm/6 text-gray-600">
                      Wer soll deine Protest-Mail bekommen?
                    </p>

                    <select
                      v-model="selectedGrouping"
                      class="block w-full appearance-none rounded border border-gray-300 bg-white px-4 py-2 pr-8 leading-tight text-gray-700 focus:border-blue-500 focus:bg-white focus:outline-none dark:border-gray-500 dark:bg-zinc-800 dark:text-white dark:ring-primary-600 dark:ring-offset-gray-700 dark:focus:border-primary-600 dark:focus:bg-zinc-800 dark:focus:ring-primary-600 dark:focus:ring-offset-gray-700"
                      name="grouping_order"
                    >
                      <option disabled>Bitte wählen</option>

                      <option
                        v-for="grouping in groupings"
                        :key="grouping.name"
                        :value="grouping.name"
                      >
                        {{ grouping.name }}
                      </option>
                    </select>
                  </template>

                  <template
                    v-if="
                      !hasGrouping ||
                      (recipientsForSelectedGrouping && recipientsForSelectedGrouping.length > 0)
                    "
                  >
                    <RadioGroup
                      v-model="form.recipient_id"
                      class="mt-2 grid w-full grid-cols-3 gap-3 sm:grid-cols-4"
                    >
                      <RadioGroupOption
                        v-for="recipient in recipientsForSelectedGrouping"
                        :key="recipient.id"
                        v-slot="{ active, checked }"
                        as="template"
                        :value="recipient.id"
                      >
                        <div
                          class="flex cursor-pointer items-center justify-center rounded-md p-3 text-sm font-semibold sm:flex-1"
                          :class="[
                            active ? 'ring-2 ring-primary-600 ring-offset-2' : '',
                            checked
                              ? 'bg-primary-600 text-white ring-0 hover:bg-primary-500'
                              : 'bg-white text-gray-900 ring-1 ring-gray-300 hover:bg-gray-50',
                            !active && !checked ? 'ring-inset' : '',
                            active && checked ? 'ring-2' : '',
                          ]"
                        >
                          {{ recipient.name }}
                          <template v-if="recipient.grouping_name"
                            >- {{ recipient.grouping_name }}</template
                          >
                        </div>
                      </RadioGroupOption>
                    </RadioGroup>
                  </template>
                </fieldset>
              </template>

              <TETopLabelLayout
                title="Vorname"
                :error="form.errors.first_name"
                name="first_name"
                required
              >
                <TETextInput
                  v-model="form.first_name"
                  name="first_name"
                  autocomplete="given-name"
                />
              </TETopLabelLayout>

              <TETopLabelLayout
                title="Nachname"
                name="last_name"
                :error="form.errors.last_name"
                required
              >
                <TETextInput v-model="form.last_name" name="last_name" autocomplete="family-name" />
              </TETopLabelLayout>

              <TETopLabelLayout
                v-if="campaign.campaign_type === 'petition'"
                title="Email"
                name="email"
                :error="form.errors.email"
                required
              >
                <TETextInput v-model="form.email" name="email" autocomplete="email" />
              </TETopLabelLayout>

              <TETopLabelLayout
                v-if="campaign.campaign_type === 'petition'"
                title="Postleitzahl"
                name="zip"
                required
                :error="form.errors.zip"
              >
                <TETextInput
                  v-model="form.zip"
                  type="number"
                  autocomplete="postal-code"
                  maxlength="5"
                  minlength="5"
                  name="zip"
                />
              </TETopLabelLayout>
            </div>

            <div class="col-span-2 mb-2">
              <span class="text-sm text-gray-500"
                ><template v-if="campaign.customization_disclaimer"
                  >{{ campaign.customization_disclaimer }}&nbsp;</template
                >

                <template v-else
                  >Ich bin damit einverstanden, dass die Unterzeichnung dieser Petition sowie meine
                  E-Mail-Adresse, Name und Ort zur Durchführung der Petition bis zu deren Ende
                  gespeichert werden. Mein Vorname, der erste Buchstabe meines Nachnamens und mein
                  Wohnort werden den Empfänger*in der Petition überreicht. Zur Verifizierung meiner
                  Daten erhalte ich eine entsprechende E-Mail mit weiteren Informationen. Diese
                  Einwilligung kann jederzeit widerrufen werden.</template
                >

                <a href="https://fridaysforfuture.de/datenschutz" class="underline"
                  >Mehr Infos</a
                ></span
              >
            </div>

            <TEAlert v-if="generalError" class="my-2" type="error" :title="generalError" />

            <TEButton
              id="sign"
              type="button"
              :disabled="form.first_name === '' || form.last_name === ''"
              class="w-full !rounded-full"
              primary
              @click="sign"
              ><span class="-m-2 text-xl font-bold uppercase">{{
                campaign.customization_button_text ?? 'Jetzt unterschreiben'
              }}</span>
            </TEButton>
          </form>
        </div>

        <div v-else class="top-0 flex flex-wrap rounded-3xl py-4">
          <div class="w-full bg-[#1b7340] px-2 text-[2.25rem] font-extrabold text-white">
            Noch ein Schritt fehlt - jetzt E-Mail
            {{ campaign.campaign_type === 'petition' ? 'bestätigen' : 'versenden' }}!
          </div>

          <div>
            <p class="mt-4">
              <template v-if="campaign.campaign_type === 'email'"
                ><b>Nicht geklappt?</b>

                <u @click="clickedFallback = true"
                  ><a :href="mailToLinkDone" target="_blank">Hier klicken!</a></u
                >

                <div v-if="clickedFallback">
                  <i>Hier kannst du den Text kopieren:</i>

                  <br />

                  <b>Empfänger:in: </b>

                  <pre>{{ props.recipients?.find((r) => r.id === form.recipient_id)?.email }}</pre>

                  <b>Betreff:</b>

                  <pre>{{ campaign.email_campaign!.subject }}</pre>

                  <b>Deine Nachricht:</b>

                  <br />

                  <pre>{{ mailTextDone }}</pre>
                </div>
              </template>

              <template v-if="campaign.customization_confirmation_text"
                >{{ campaign.customization_confirmation_text }}
              </template>

              <template v-else>
                Rufe deine E-Mail jetzt ab und klicke auf den Bestätigungslink. Nur dann können wir
                deine Stimme zählen! Die Bestätigung stellt sicher, dass jede Unterschrift
                tatsächlich von einem Menschen kommt.
              </template>
            </p>

            <p class="whitespace-pre-wrap">{{ campaign.email_footer }}</p>
          </div>
        </div>
      </TransitionGroup>
    </main>

    <div v-else class="box my-4 rounded-3xl bg-white p-6">
      <h1 class="text-2xl font-bold">Diese Aktion ist leider nicht mehr aktiv.</h1>

      <p class="mt-4">
        Diese Aktion ist leider nicht mehr aktiv. Schau dich gerne auf
        <a href="https://fridaysforfuture.de/" class="underline" target="_blank"
          >fridaysforfuture.de</a
        >
        um, dort findest du unsere aktuellen Kampagnen und Aktionen.
      </p>
    </div>
  </div>
</template>

<style>
.box {
  box-shadow: 1px 3px 5px 0 rgba(0, 0, 0, 0.3);
}

li {
  list-style-type: disc;
  margin-left: 2rem;
}

p + p {
  margin-top: 1rem;
}

body {
  @apply !bg-white;
}
</style>
